import { Group, SegmentedControl } from "@mantine/core";
import { useEffect } from "react";

export type TrendRange = "today" | "24h" | "7d" | "14d" | "30d";
export type TrendGranularity = "day" | "hour";

const TREND_RANGES = [
  { label: "业务今日", value: "today" },
  { label: "近 24 小时(滚动)", value: "24h" },
  { label: "近 7 业务日", value: "7d" },
  { label: "近 14 业务日", value: "14d" },
  { label: "近 30 业务日", value: "30d" },
] as const;

const TREND_GRANULARITY = [
  { label: "按天", value: "day" },
  { label: "按小时", value: "hour" },
] as const;

export const granularityLabel = (g: TrendGranularity): string => (g === "hour" ? "小时" : "天");

/** 与 domain statsRangeLabel 对齐：业务今日 / 滚动 24h / 业务日 */
export const trendRangeLabel = (range: TrendRange): string => {
  switch (range) {
    case "today":
      return "业务今日";
    case "24h":
      return "近 24 小时(滚动)";
    case "7d":
      return "近 7 业务日";
    case "14d":
      return "近 14 业务日";
    case "30d":
      return "近 30 业务日";
  }
};

export const statsRangeHint = (range: TrendRange): string => {
  switch (range) {
    case "today":
      return "按业务时区自然日 0:00 起至现在（非浏览器本地日）";
    case "24h":
      return "按墙上时钟从现在往回 24 小时（可跨业务昨日）";
    case "7d":
    case "14d":
    case "30d":
      return "按业务时区自然日回溯，含业务今日";
  }
};

/** 仅今天 / 近 24 小时可按小时；7d/14d/30d 只按天（与 domain resolveEffectiveGranularity 对齐）。 */
const isHourOnlyRange = (range: TrendRange): boolean => range === "today" || range === "24h";

const isHourGranularityAllowed = (range: TrendRange): boolean => isHourOnlyRange(range);

/** 与后端 resolveEffectiveGranularity 对齐，供请求参数与 queryKey 使用。 */
export const resolveTrendGranularity = (
  range: TrendRange,
  _granularity: TrendGranularity,
): TrendGranularity => {
  if (isHourOnlyRange(range)) return "hour";
  return "day";
};

type Props = {
  readonly range: TrendRange;
  readonly granularity: TrendGranularity;
  readonly onRangeChange: (range: TrendRange) => void;
  readonly onGranularityChange: (granularity: TrendGranularity) => void;
  readonly hideGranularity?: boolean;
};

export function StatsTrendControls(props: Props) {
  const { range, granularity, onRangeChange, onGranularityChange, hideGranularity = false } = props;
  // 多日区间不展示粒度切换（只有按天）；短区间固定按小时也不展示切换
  const showGranularity = !hideGranularity && isHourGranularityAllowed(range);

  useEffect(() => {
    const effective = resolveTrendGranularity(range, granularity);
    if (effective !== granularity) {
      onGranularityChange(effective);
    }
  }, [range, granularity, onGranularityChange]);

  const handleRangeChange = (next: TrendRange) => {
    const nextGranularity = resolveTrendGranularity(next, granularity);
    if (nextGranularity !== granularity) {
      onGranularityChange(nextGranularity);
    }
    onRangeChange(next);
  };

  return (
    <Group gap="sm" wrap="wrap">
      <SegmentedControl
        value={range}
        onChange={(v) => handleRangeChange(v as TrendRange)}
        data={[...TREND_RANGES]}
      />
      {showGranularity ? (
        <SegmentedControl
          value={granularity}
          onChange={(v) => onGranularityChange(v as TrendGranularity)}
          data={TREND_GRANULARITY.map((it) => ({
            ...it,
            // 今天 / 24h：只能按小时，禁用「按天」
            disabled: it.value === "day",
          }))}
        />
      ) : null}
    </Group>
  );
}
